// libs
import * as React from "react";
import {
  Link,
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  InputBase,
  Button,
  Stack,
  Pagination,
  Select,
  MenuItem,
} from "@mui/material";
import moment from "moment";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUp from "@mui/icons-material/ArrowDropUp";
import { AuthenticationContext } from "../Context";
import { getHeaders } from "../utils/HeaderHelper";
import DatePickerWithLabel from "../common/DatePickerWithLabel";
import LoadingReportsTable from "../shared/LoadingReportsTable";
import EmptyReportsTable from "../shared/EmptyReportsTable";
import BadgeList from "../common/BadgeList";
import { toTitleCase } from "../utils/CaseFormatHelper";
import InterventionsDetailAdvancedFilters from "./InterventionsDetailAdvancedFilters";

interface ChipData {
  key: string;
  label: string;
  type: "payer" | "payer_program" | "creator" | "signer" | "intervention_type" | "status";
}

interface FilterOption {
  id: string;
  name: string;
}

interface FilterOptions {
  payers: FilterOption[];
  payer_programs: FilterOption[];
  creators: FilterOption[];
  signers: FilterOption[];
  intervention_types: FilterOption[];
  statuses: FilterOption[];
}

interface ReportRow {
  patient_id: number;
  intervention_id: number;
  patient_name: string;
  date_of_birth: string;
  member_unique_id: string;
  enrolled_payer_programs: string;
  payer: string;
  created_date: string;
  creator: string;
  signed_date: string;
  signer: string;
  status: string;
  intervention_type: string;
  activity: string;
  related_condition_dx: string;
  related_medication: string;
  related_device: string;
  notes: string;
  annual_hard_savings: string | number | null;
  has_prescriber_response: string | null;
}

interface NoteProps {
  id: number;
  note: string;
  isExpanded: boolean;
  onToggle: (id: number) => void;
}

const NoteCell: React.FC<NoteProps> = ({ id, note, isExpanded, onToggle }) => {
  if (!note) return <>N/A</>;
  const maxLength = 60;

  if (note.length <= maxLength) return <>{note}</>;

  return (
    <>
      {isExpanded ? note : `${note.substring(0, maxLength)}... `}
      <Link
        onClick={() => onToggle(id)}
        sx={{
          cursor: "pointer",
          fontSize: "0.85rem",
          ml: 0.5,
          fontWeight: "bold",
          color: "#1976d2",
          textDecorationColor: "#1976d2",
        }}
      >
        {isExpanded ? "Show Less" : "Read More"}
      </Link>
    </>
  );
};

const InterventionsDetailReport: React.FC = () => {
  const authenticationSetting = React.useContext(AuthenticationContext);

  const isInitialMount = React.useRef(true);
  const lastNameDebounce = React.useRef<NodeJS.Timeout | null>(null);

  // Filters
  const [startDate, setStartDate] = React.useState<string>(
    moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = React.useState<string>(
    moment().subtract(1, "month").endOf("month").format("YYYY-MM-DD")
  );
  const [lastName, setLastName] = React.useState<string>("");
  const [debouncedLastName, setDebouncedLastName] = React.useState<string>("");

  // Advanced search
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedPayers, setSelectedPayers] = React.useState<string[]>([]);
  const [selectedPayerPrograms, setSelectedPayerPrograms] = React.useState<string[]>([]);
  const [selectedCreators, setSelectedCreators] = React.useState<string[]>([]);
  const [selectedSigners, setSelectedSigners] = React.useState<string[]>([]);
  const [selectedInterventionTypes, setSelectedInterventionTypes] = React.useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = React.useState<string[]>([]);
  const [badgeData, setBadgeData] = React.useState<ChipData[]>([]);

  // Data
  const [filterOptions, setFilterOptions] = React.useState<FilterOptions>({
    payers: [],
    payer_programs: [],
    creators: [],
    signers: [],
    intervention_types: [],
    statuses: [],
  });
  const [tableData, setTableData] = React.useState<ReportRow[]>([]);
  const [totalCount, setTotalCount] = React.useState<number>(0);
  const [pageCount, setPageCount] = React.useState<number>(0);
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [perPage, setPerPage] = React.useState<number>(100);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [expandedNotes, setExpandedNotes] = React.useState<Record<number, boolean>>({});

  // Debounce last name
  React.useEffect(() => {
    if (lastNameDebounce.current) clearTimeout(lastNameDebounce.current);
    lastNameDebounce.current = setTimeout(() => {
      if (lastName.length === 0 || lastName.length >= 2) {
        setDebouncedLastName(lastName);
        setCurrentPage(1);
      }
    }, 400);
    return () => { if (lastNameDebounce.current) clearTimeout(lastNameDebounce.current); };
  }, [lastName]);

  // Reset page when filters change
  React.useEffect(() => {
    if (isInitialMount.current) return;
    setCurrentPage(1);
  }, [
    startDate, endDate,
    selectedPayers, selectedPayerPrograms, selectedCreators, selectedSigners,
    selectedInterventionTypes, selectedStatuses, perPage,
  ]);

  // Fetch data
  React.useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    }
    getData(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    startDate, endDate, debouncedLastName,
    selectedPayers, selectedPayerPrograms, selectedCreators, selectedSigners,
    selectedInterventionTypes, selectedStatuses,
    currentPage, perPage,
  ]);

  // Sync badges
  React.useEffect(() => {
    const payerMap = Object.fromEntries(filterOptions.payers.map((p) => [p.id, p.name]));
    const ppMap = Object.fromEntries(filterOptions.payer_programs.map((p) => [p.id, p.name]));
    const creatorMap = Object.fromEntries(filterOptions.creators.map((c) => [c.id, c.name]));
    const signerMap = Object.fromEntries(filterOptions.signers.map((s) => [s.id, s.name]));
    const typeMap = Object.fromEntries(filterOptions.intervention_types.map((t) => [t.id, t.name]));
    const statusMap = Object.fromEntries(filterOptions.statuses.map((s) => [s.id, s.name]));
    const badges: ChipData[] = [
      ...selectedPayers.map((id) => ({ key: id, label: payerMap[id] ?? `Payer ${id}`, type: "payer" as const })),
      ...selectedPayerPrograms.map((id) => ({ key: id, label: ppMap[id] ?? `Program ${id}`, type: "payer_program" as const })),
      ...selectedCreators.map((id) => ({ key: id, label: creatorMap[id] ?? `Creator ${id}`, type: "creator" as const })),
      ...selectedSigners.map((id) => ({ key: id, label: signerMap[id] ?? `Signer ${id}`, type: "signer" as const })),
      ...selectedInterventionTypes.map((id) => ({ key: id, label: typeMap[id] ?? id, type: "intervention_type" as const })),
      ...selectedStatuses.map((id) => ({ key: id, label: statusMap[id] ?? id, type: "status" as const })),
    ];
    setBadgeData(badges);
  }, [
    selectedPayers, selectedPayerPrograms, selectedCreators, selectedSigners,
    selectedInterventionTypes, selectedStatuses, filterOptions,
  ]);

  const handleDeleteBadge = (badge: ChipData) => {
    if (badge.type === "payer") setSelectedPayers((p) => p.filter((id) => id !== badge.key));
    else if (badge.type === "payer_program") setSelectedPayerPrograms((p) => p.filter((id) => id !== badge.key));
    else if (badge.type === "creator") setSelectedCreators((p) => p.filter((id) => id !== badge.key));
    else if (badge.type === "signer") setSelectedSigners((p) => p.filter((id) => id !== badge.key));
    else if (badge.type === "intervention_type") setSelectedInterventionTypes((p) => p.filter((id) => id !== badge.key));
    else if (badge.type === "status") setSelectedStatuses((p) => p.filter((id) => id !== badge.key));
  };

  const buildParams = (page: number) => {
    const p = new URLSearchParams({
      start_date: startDate || "",
      end_date: endDate || "",
      last_name: debouncedLastName.length >= 2 ? debouncedLastName : "",
      page: String(page),
      per_page: String(perPage),
    });
    selectedPayers.forEach((id) => p.append("payer_ids[]", id));
    selectedPayerPrograms.forEach((id) => p.append("payer_program_ids[]", id));
    selectedCreators.forEach((id) => p.append("creator_ids[]", id));
    selectedSigners.forEach((id) => p.append("signer_ids[]", id));
    selectedInterventionTypes.forEach((id) => p.append("intervention_types[]", id));
    selectedStatuses.forEach((id) => p.append("statuses[]", id));
    return p;
  };

  const getData = (page: number) => {
    setLoading(true);
    fetch(
      `/admin/reports/interventions_detail/report_data?${buildParams(page)}`,
      { method: "GET", headers: getHeaders(authenticationSetting.csrfToken) }
    )
      .then((res) => res.json())
      .then((result) => {
        if (!result.success) {
          console.error(result.error);
        } else {
          const resource = result?.resource || {};
          setTableData(resource.results || []);
          setTotalCount(resource.total_count || 0);
          setPageCount(resource.total_pages || 0);
          if (resource.filter_options) setFilterOptions(resource.filter_options);
        }
      })
      .catch((err) => console.error("Failed to fetch report data:", err))
      .finally(() => setLoading(false));
  };

  const selectPage = (page: number) => {
    if (page < 1 || page > pageCount) return;
    setCurrentPage(page);
  };

  const handleExportCsv = () => {
    const params = buildParams(1);
    params.delete("page");
    window.location.href = `/admin/reports/interventions_detail/generate_csv.csv?${params}`;
  };

  const toggleNoteExpand = (id: number) => {
    setExpandedNotes((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const hasBadges = badgeData.length > 0;

  return (
    <Box className="report-main-content" sx={{ paddingLeft: "120px !important" }}>
      {/* ── Header ── */}
      <Box className="main-header">
        <Box className="heading-with-back-button-section">
          <Stack direction="row" columnGap="12px">
            <Link style={{ textDecoration: "none", display: "flex", alignItems: "center" }} href="/reports">
              <ArrowBackIosNewIcon
                style={{
                  borderRadius: "3px", padding: "8px", color: "black",
                  boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.2)", display: "flex",
                  alignItems: "center", justifyContent: "center", cursor: "pointer",
                  transition: "all 0.3s ease", fontSize: "14px",
                }}
              />
            </Link>
            <Typography className="main-heading">Interventions Detail Report</Typography>
          </Stack>
        </Box>
      </Box>

      {/* ── Content card ── */}
      <Box className="report-details">
        <Box className="sub-header">
          <Box className="subheading-with-pill-section">
            <Typography className="sub-heading">Report Details</Typography>
            <Box className="count-pill">{totalCount}</Box>
          </Box>

          <Box className="filters-section">
            <Paper className="input-paper">
              <InputBase
                placeholder="Search Last Name"
                inputProps={{ "aria-label": "search last name" }}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
              <SearchIcon sx={{ color: "#707070" }} />
            </Paper>

            <DatePickerWithLabel
              date={startDate}
              setDate={setStartDate}
              initialDate={moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD")}
              label="Report Period Start Date"
            />
            <DatePickerWithLabel
              date={endDate}
              setDate={setEndDate}
              initialDate={moment().subtract(1, "month").endOf("month").format("YYYY-MM-DD")}
              label="Report Period End Date"
            />

            <div
              className="advanced-search-container"
              onClick={loading ? undefined : (e) => setAnchorEl(e.currentTarget)}
              style={{ cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.8 : 1 }}
            >
              <FilterListIcon sx={{ width: 20, height: 20 }} />
              <p className="advanced-search-text">Advanced Search</p>
              {anchorEl ? <ArrowDropUp sx={{ width: 24, height: 24 }} /> : <ArrowDropDownIcon sx={{ width: 24, height: 24 }} />}
              <InterventionsDetailAdvancedFilters
                anchorEl={anchorEl}
                setAnchorEl={setAnchorEl}
                payerOptions={filterOptions.payers}
                payerProgramOptions={filterOptions.payer_programs}
                creatorOptions={filterOptions.creators}
                signerOptions={filterOptions.signers}
                interventionTypeOptions={filterOptions.intervention_types}
                statusOptions={filterOptions.statuses}
                selectedPayers={selectedPayers}
                setSelectedPayers={setSelectedPayers}
                selectedPayerPrograms={selectedPayerPrograms}
                setSelectedPayerPrograms={setSelectedPayerPrograms}
                selectedCreators={selectedCreators}
                setSelectedCreators={setSelectedCreators}
                selectedSigners={selectedSigners}
                setSelectedSigners={setSelectedSigners}
                selectedInterventionTypes={selectedInterventionTypes}
                setSelectedInterventionTypes={setSelectedInterventionTypes}
                selectedStatuses={selectedStatuses}
                setSelectedStatuses={setSelectedStatuses}
                disabled={loading}
              />
            </div>

            <Button className="export-csv-button" onClick={handleExportCsv} disabled={totalCount === 0}>
              Export as CSV
            </Button>
          </Box>
        </Box>

        {/* ── Table ── */}
        <Box className="table-section">
          {hasBadges && (
            <div className="badge-container" style={{ width: "auto", marginBottom: "20px" }}>
              <BadgeList
                badgeData={badgeData}
                setBadgeData={setBadgeData}
                handleDeleteBadge={handleDeleteBadge}
                background="none"
              />
            </div>
          )}

          {loading ? (
            <LoadingReportsTable isBadgeShowing={hasBadges} />
          ) : (
            <>
              {tableData.length > 0 ? (
                <TableContainer
                  className="report-table-container"
                  style={{ height: hasBadges ? "45vh" : "53.8vh" }}
                >
                  <Table stickyHeader>
                    <TableHead className="table-header">
                      <TableRow sx={{ "& td, & th": { borderBottom: "none" } }}>
                        <TableCell align="left" sx={{ minWidth: "160px" }}>Patient Name</TableCell>
                        <TableCell align="left" sx={{ minWidth: "110px" }}>DOB</TableCell>
                        <TableCell align="left" sx={{ minWidth: "140px" }}>Member Unique ID</TableCell>
                        <TableCell align="left" sx={{ minWidth: "200px" }}>Enrolled Payor Program(s)</TableCell>
                        <TableCell align="left" sx={{ minWidth: "140px" }}>Payer</TableCell>
                        <TableCell align="left" sx={{ minWidth: "130px" }}>Created Date</TableCell>
                        <TableCell align="left" sx={{ minWidth: "140px" }}>Creator</TableCell>
                        <TableCell align="left" sx={{ minWidth: "130px" }}>Signed Date</TableCell>
                        <TableCell align="left" sx={{ minWidth: "140px" }}>Signer</TableCell>
                        <TableCell align="left" sx={{ minWidth: "100px" }}>Status</TableCell>
                        <TableCell align="left" sx={{ minWidth: "120px" }}>Type</TableCell>
                        <TableCell align="left" sx={{ minWidth: "140px" }}>Activity</TableCell>
                        <TableCell align="left" sx={{ minWidth: "180px" }}>Related Condition/Dx</TableCell>
                        <TableCell align="left" sx={{ minWidth: "160px" }}>Medication Involved</TableCell>
                        <TableCell align="left" sx={{ minWidth: "140px" }}>Device Involved</TableCell>
                        <TableCell align="left" sx={{ minWidth: "240px" }}>Notes</TableCell>
                        <TableCell align="left" sx={{ minWidth: "150px" }}>Annual Hard Savings</TableCell>
                        <TableCell align="left" sx={{ minWidth: "200px" }}>Received Prescriber Response</TableCell>
                        <TableCell align="left" sx={{ minWidth: "140px" }}>{/* View Intervention — no header */}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tableData.map((row) => (
                        <TableRow key={row.intervention_id} sx={{ "& td, & th": { borderBottom: "none" } }}>
                          <TableCell align="left">
                            <Link
                              href={`/patient_reports/${row.patient_id}/action_center`}
                              sx={{ color: "black", textDecorationColor: "black", textDecorationLine: "underline", cursor: "pointer" }}
                            >
                              {row.patient_name}
                            </Link>
                          </TableCell>
                          <TableCell align="left">{row.date_of_birth}</TableCell>
                          <TableCell align="left">{row.member_unique_id}</TableCell>
                          <TableCell align="left">{row.enrolled_payer_programs}</TableCell>
                          <TableCell align="left">{row.payer}</TableCell>
                          <TableCell align="left">{row.created_date}</TableCell>
                          <TableCell align="left">{row.creator}</TableCell>
                          <TableCell align="left">{row.signed_date}</TableCell>
                          <TableCell align="left">{row.signer}</TableCell>
                          <TableCell align="left">{toTitleCase(row.status)}</TableCell>
                          <TableCell align="left">{row.intervention_type || "N/A"}</TableCell>
                          <TableCell align="left">{row.activity || "N/A"}</TableCell>
                          <TableCell align="left">{row.related_condition_dx}</TableCell>
                          <TableCell align="left">{row.related_medication || "N/A"}</TableCell>
                          <TableCell align="left">{row.related_device || "N/A"}</TableCell>
                          <TableCell align="left">
                            <NoteCell
                              id={row.intervention_id}
                              note={row.notes}
                              isExpanded={!!expandedNotes[row.intervention_id]}
                              onToggle={toggleNoteExpand}
                            />
                          </TableCell>
                          <TableCell align="left">
                            {row.annual_hard_savings !== null && row.annual_hard_savings !== undefined
                              ? `$${row.annual_hard_savings}`
                              : "N/A"}
                          </TableCell>
                          <TableCell align="left">{row.has_prescriber_response || "N/A"}</TableCell>
                          <TableCell align="left">
                            <Link
                              href={`/patient_reports/${row.patient_id}/interventions/new?intervention_id=${row.intervention_id}&view_only=1`}
                              sx={{
                                display: "inline-flex", alignItems: "center", gap: "4px",
                                color: "#0B34D8", fontSize: "13px", fontWeight: 500,
                                textDecorationLine: "underline", cursor: "pointer",
                              }}
                            >
                              View Intervention
                              <OpenInNewIcon sx={{ fontSize: 14 }} />
                            </Link>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <EmptyReportsTable isBadgeShowing={hasBadges} />
              )}
            </>
          )}

          {/* Pagination */}
          {tableData.length > 0 && !loading && (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2, marginTop: 2 }}>
              <Typography sx={{ fontSize: "14px" }}>Rows per page</Typography>
              <Select
                value={perPage}
                onChange={(e) => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}
                disabled={loading}
                size="small"
                sx={{
                  minWidth: 70,
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "none" },
                }}
              >
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={150}>150</MenuItem>
                <MenuItem value={200}>200</MenuItem>
              </Select>
              {pageCount > 0 && (
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Pagination
                    count={pageCount}
                    page={currentPage}
                    onChange={(_e, page) => selectPage(page)}
                    boundaryCount={1}
                    siblingCount={1}
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default InterventionsDetailReport;
